import { isCircuitExternalJob } from '../../../common/reportOutcome'

/**
 * Maps a job's current status CODE (`row.status`) to the status-change actions
 * available in the Planned Jobs widget. Each action carries the target status
 * CODE sent through `useJobStatusChange`, plus the operator's INTENT (see below).
 */

export const TARGET_STATUS_CODE = {
  START: 'ACTIVE_EXECUTION',
  PREPARATION: 'ACTIVE_PREPARATION',
  INTERRUPT: 'INTERRUPTED',
  FINISH: 'FINISHED'
}

/**
 * Story 113 (S114, R-F) — the operator's INTENT, declared apart from the target status code.
 *
 * `statusCode` says WHICH STATE the job goes to. The intent says WHAT THE OPERATOR IS DOING. Until
 * this story the two were the same thing, because `TARGET_STATUS_CODE.START` *is* `'ACTIVE_EXECUTION'`
 * and three sites compared against it to decide things that are not "which state am I going to":
 *
 *   1. `useJobStatusChange` — do I check whether the operator has an open non-productive task?
 *   2. `PlannedJobListWidget` — do I auto-select the job on the station when it succeeds?
 *   3. `PlannedJobCustomModal` — the same, from the "Show all" modal.
 *
 * Once a job can start in preparation, that conflation breaks in two concrete ways: starting in
 * `ACTIVE_PREPARATION` would NOT auto-select the job (S115), and a preparation -> execution
 * transition WOULD raise the "stop the task and join" dialog mid-job (S117). Both decisions belong to
 * the intent, and the two predicates below are the only place they are taken.
 *
 * `START` deliberately covers starting, reopening, joining AND RESUMING (S166-S168, enmienda 01): a
 * paused job is offered the very same action object as a job about to start, so resuming needs no
 * parallel path — which is exactly what R-F asks for.
 */
export const JOB_ACTION_INTENT = {
  START: 'START',
  GO_TO_EXECUTION: 'GO_TO_EXECUTION',
  BACK_TO_PREPARATION: 'BACK_TO_PREPARATION',
  PAUSE: 'PAUSE',
  FINISH: 'FINISH'
}

/**
 * The two modes the start-mode modal offers (S107). The values are the status codes themselves, so
 * the modal never has to know how to translate its own options.
 */
export const JOB_START_MODE = {
  PREPARATION: TARGET_STATUS_CODE.PREPARATION,
  EXECUTION: TARGET_STATUS_CODE.START
}

/**
 * The single place that translates intent -> target status code.
 *
 * Only `START` takes a mode, and only because the operator chose it in the modal; with no choice
 * (the job does not manage preparation, so no modal opened) it falls back to execution, exactly as
 * before the story (S111, S167).
 *
 * @param {string} intent One of JOB_ACTION_INTENT
 * @param {string} [chosenStartMode] One of JOB_START_MODE, only meaningful for START
 * @returns {string} The status code to send in `updateStatus`
 */
export const resolveTargetStatusCode = (intent, chosenStartMode = '') => {
  switch (intent) {
    case JOB_ACTION_INTENT.START:
      return chosenStartMode === JOB_START_MODE.PREPARATION
        ? TARGET_STATUS_CODE.PREPARATION
        : TARGET_STATUS_CODE.START
    case JOB_ACTION_INTENT.GO_TO_EXECUTION:
      return TARGET_STATUS_CODE.START
    case JOB_ACTION_INTENT.BACK_TO_PREPARATION:
      return TARGET_STATUS_CODE.PREPARATION
    case JOB_ACTION_INTENT.PAUSE:
      return TARGET_STATUS_CODE.INTERRUPT
    case JOB_ACTION_INTENT.FINISH:
      return TARGET_STATUS_CODE.FINISH
    default:
      return ''
  }
}

/**
 * S116/S117 — whether this action must first check the operator's open non-productive task. Only a
 * start does: a mode transition happens mid-job, with the operator already working on it, so the
 * "stop the task and join" offer does not apply.
 *
 * Note what this does NOT look at: the target status code. `GO_TO_EXECUTION` resolves to exactly the
 * same code as a plain start, which is why deciding by code cannot work.
 */
export const requiresNonProductiveTaskCheck = (intent) => intent === JOB_ACTION_INTENT.START

/**
 * S115 — whether a successful action should leave the job selected on the station. Only a start
 * does, whichever mode it started in: the station must never be left with no selected job because
 * the operator chose preparation.
 */
export const selectsJobOnSuccess = (intent) => intent === JOB_ACTION_INTENT.START

const START = { label: 'Start', statusCode: TARGET_STATUS_CODE.START, intent: JOB_ACTION_INTENT.START }
const INTERRUPT = { label: 'Interrupt', statusCode: TARGET_STATUS_CODE.INTERRUPT, intent: JOB_ACTION_INTENT.PAUSE }
const FINISH = { label: 'Finish', statusCode: TARGET_STATUS_CODE.FINISH, intent: JOB_ACTION_INTENT.FINISH }

const STATUS_ACTIONS = {
  PENDING: [START],
  ACTIVE_EXECUTION: [FINISH, INTERRUPT],
  // D9 (signed contradiction, RN39.1): the row actions do NOT change for a job in preparation. Only
  // the Status column distinguishes the two active modes. Accepted consequence: finishing a job in
  // preparation from these listings stays possible (S147, S148).
  ACTIVE_PREPARATION: [FINISH, INTERRUPT],
  // S166-S168: the Start offered here is a RESUME, and it carries the START intent for that reason.
  INTERRUPTED: [START, FINISH],
  WAITING: [START],
  // A finished job can be re-started (rework / redo), which is also the whole point of the
  // "Finished Compatible Jobs" tab in the Show-all modal. The backend validates the transition.
  FINISHED: [START]
}

/**
 * @param {string} status - The row's current status CODE
 * @returns {Array<{label: string, statusCode: string, intent: string}>} Available actions
 */
export const getJobStatusActions = (status) => STATUS_ACTIONS[status] || []

/**
 * Stories 86 (S48) and 100 (S75): a job governed by the acceptance form, or one that crossed the
 * external circuit gate, is offered no status-change action, and the disabled Start says why. The two
 * gates are mutually exclusive in practice, so only one tooltip ever applies.
 *
 * Story 160 (R-16): the single place that decides it. The Planned Jobs row and the job detail header
 * both read it; neither copies the condition.
 *
 * @param {Object} job A Planned Jobs row or the `job` of the job detail (both publish the two flags)
 * @returns {{ gated: boolean, tooltipKey: string }} `tooltipKey` is an i18n key, '' when not gated
 */
export const resolveJobActionGate = (job = {}) => {
  if (job.governedByAcceptance === true) {
    return { gated: true, tooltipKey: 'Acceptance job: actions are governed by the acceptance form' }
  }
  if (isCircuitExternalJob(job)) {
    return { gated: true, tooltipKey: 'External job: actions are governed by the external jobs widget' }
  }
  return { gated: false, tooltipKey: '' }
}
